"use client"

import { useCallback, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { ArrowRight, Search } from "lucide-react"
import { useCatalogTransition } from "@/components/public/CatalogTransitionContext"
import { useTranslations } from "@/lib/i18n/client"
import {
  cn,
  FILTER_PENDING_CLASSES,
  getFabricanteLabel,
} from "@/lib/utils"
import { resolveI18n } from "@/lib/i18n/resolve"
import type { I18nText } from "@/lib/types"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { RangeDualSlider } from "@/components/public/RangeDualSlider"

export const YEAR_MIN = 1980
export const YEAR_MAX = new Date().getFullYear() + 1

interface CategoriaOption {
  slug: string
  nome: I18nText
}

interface FabricanteOption {
  slug: string
  nome: string
}

interface ModeloOption {
  slug: string
  nome: I18nText
  fabricante_slug: string
  fabricante_nome: string
  categoria_slug: string
}

interface CatalogFiltersPanelProps {
  categorias: CategoriaOption[]
  fabricantes: FabricanteOption[]
  modelos: ModeloOption[]
  localizacoes: string[]
  isHelicopteros: boolean
  mode?: "immediate" | "staged"
  stagedParams?: URLSearchParams
  onStagedChange?: (params: URLSearchParams) => void
}

function parseList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

export function CatalogFiltersPanel({
  categorias,
  fabricantes,
  modelos,
  localizacoes,
  isHelicopteros,
  mode = "immediate",
  stagedParams,
  onStagedChange,
}: CatalogFiltersPanelProps) {
  const { locale, t } = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { isPending, startTransition } = useCatalogTransition()
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)

  const isStaged = mode === "staged" && !!onStagedChange
  const effectiveParams: URLSearchParams | ReturnType<typeof useSearchParams> =
    isStaged && stagedParams ? stagedParams : searchParams

  const categoriaSel = useMemo(
    () => parseList(effectiveParams.get("categoria") ?? ""),
    [effectiveParams],
  )
  const fabricanteSel = useMemo(
    () => parseList(effectiveParams.get("fabricante") ?? ""),
    [effectiveParams],
  )
  const modeloSel = useMemo(
    () => parseList(effectiveParams.get("modelo") ?? ""),
    [effectiveParams],
  )
  const localizacaoSel = useMemo(
    () => parseList(effectiveParams.get("localizacao") ?? ""),
    [effectiveParams],
  )
  // Cross-filter: categorias visíveis dado fabricante + modelo selecionados
  // (ignore-self pattern: a própria seleção de categoria não é aplicada aqui).
  // Também esconde categorias sem aeronaves publicadas — alinhado com fabricantes/modelos.
  const visibleCategorias = useMemo(() => {
    const categoriasComAeronave = new Set(modelos.map((m) => m.categoria_slug))
    const validForCross = new Set<string>()
    for (const m of modelos) {
      if (
        fabricanteSel.length &&
        !fabricanteSel.includes(m.fabricante_slug)
      ) {
        continue
      }
      if (modeloSel.length && !modeloSel.includes(m.slug)) continue
      validForCross.add(m.categoria_slug)
    }
    return categorias.filter((c) => {
      if (!categoriasComAeronave.has(c.slug)) return false
      if (categoriaSel.includes(c.slug)) return true
      return validForCross.has(c.slug)
    })
  }, [categorias, modelos, fabricanteSel, modeloSel, categoriaSel])

  // Cross-filter: fabricantes visíveis dado categoria + modelo selecionados.
  const visibleFabricantes = useMemo(() => {
    const validForCross = new Set<string>()
    for (const m of modelos) {
      if (categoriaSel.length && !categoriaSel.includes(m.categoria_slug)) {
        continue
      }
      if (modeloSel.length && !modeloSel.includes(m.slug)) continue
      validForCross.add(m.fabricante_slug)
    }
    return fabricantes.filter((f) => {
      if (fabricanteSel.includes(f.slug)) return true
      return validForCross.has(f.slug)
    })
  }, [fabricantes, modelos, categoriaSel, modeloSel, fabricanteSel])

  // Cross-filter: modelos visíveis dado categoria + fabricante selecionados.
  // Modelo selecionado fica sticky para o usuário conseguir desmarcá-lo via UI.
  const visibleModelos = useMemo(
    () =>
      modelos.filter((m) => {
        if (modeloSel.includes(m.slug)) return true
        if (categoriaSel.length && !categoriaSel.includes(m.categoria_slug)) {
          return false
        }
        if (
          fabricanteSel.length &&
          !fabricanteSel.includes(m.fabricante_slug)
        ) {
          return false
        }
        return true
      }),
    [modelos, categoriaSel, fabricanteSel, modeloSel],
  )
  const currentQ = effectiveParams.get("q") ?? ""
  const anoMin = Number(effectiveParams.get("ano_min")) || YEAR_MIN
  const anoMax = Number(effectiveParams.get("ano_max")) || YEAR_MAX
  const apenasDestaque = effectiveParams.get("destaque") === "1"
  const comFotos = effectiveParams.get("com_fotos") === "1"

  const [searchValue, setSearchValue] = useState(currentQ)

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      const base = isStaged && stagedParams ? stagedParams : searchParams
      const params = new URLSearchParams(base.toString())
      for (const [key, value] of Object.entries(updates)) {
        if (value) params.set(key, value)
        else params.delete(key)
      }
      params.delete("pagina")
      if (isStaged && onStagedChange) {
        onStagedChange(params)
        return
      }
      const qs = params.toString()
      startTransition(() => {
        router.push(qs ? `/aeronaves?${qs}` : "/aeronaves")
      })
    },
    [
      isStaged,
      stagedParams,
      onStagedChange,
      router,
      searchParams,
      startTransition,
    ],
  )

  // Ao mudar categoria ou fabricante, descarta fabricantes/modelos órfãos
  // (que não pertencem mais ao novo escopo) numa única atualização da URL.
  const applyParentFilterChange = useCallback(
    (key: "categoria" | "fabricante", nextValues: string[]) => {
      const nextCategoriaSel =
        key === "categoria" ? nextValues : categoriaSel
      let nextFabricanteSel =
        key === "fabricante" ? nextValues : fabricanteSel
      let nextModeloSel = modeloSel

      if (key === "categoria" && nextFabricanteSel.length > 0) {
        const validFabricantes = new Set<string>()
        for (const m of modelos) {
          if (
            nextCategoriaSel.length &&
            !nextCategoriaSel.includes(m.categoria_slug)
          ) {
            continue
          }
          validFabricantes.add(m.fabricante_slug)
        }
        nextFabricanteSel = nextFabricanteSel.filter((s) =>
          validFabricantes.has(s),
        )
      }

      if (nextModeloSel.length > 0) {
        const validModelos = new Set<string>()
        for (const m of modelos) {
          if (
            nextCategoriaSel.length &&
            !nextCategoriaSel.includes(m.categoria_slug)
          ) {
            continue
          }
          if (
            nextFabricanteSel.length &&
            !nextFabricanteSel.includes(m.fabricante_slug)
          ) {
            continue
          }
          validModelos.add(m.slug)
        }
        nextModeloSel = nextModeloSel.filter((s) => validModelos.has(s))
      }

      const updates: Record<string, string> = {
        [key]: nextValues.join(","),
      }
      if (nextFabricanteSel.length !== fabricanteSel.length) {
        updates.fabricante = nextFabricanteSel.join(",")
      }
      if (nextModeloSel.length !== modeloSel.length) {
        updates.modelo = nextModeloSel.join(",")
      }
      updateParams(updates)
    },
    [categoriaSel, fabricanteSel, modeloSel, modelos, updateParams],
  )

  const toggleIn = useCallback(
    (
      key: "categoria" | "fabricante" | "modelo",
      slug: string,
      checked: boolean,
    ) => {
      const current =
        key === "categoria"
          ? categoriaSel
          : key === "fabricante"
            ? fabricanteSel
            : modeloSel
      const next = checked
        ? Array.from(new Set([...current, slug]))
        : current.filter((s) => s !== slug)
      if (key === "modelo") {
        updateParams({ modelo: next.join(",") })
        return
      }
      applyParentFilterChange(key, next)
    },
    [
      categoriaSel,
      fabricanteSel,
      modeloSel,
      updateParams,
      applyParentFilterChange,
    ],
  )

  // Localização é eixo independente (sem cascata). Valor é texto livre, então
  // o toggle compara case-insensitive para casar duplicatas só de caixa.
  const toggleLocalizacao = useCallback(
    (loc: string, checked: boolean) => {
      const next = checked
        ? Array.from(new Set([...localizacaoSel, loc]))
        : localizacaoSel.filter(
            (s) => s.toLowerCase() !== loc.toLowerCase(),
          )
      updateParams({ localizacao: next.join(",") })
    },
    [localizacaoSel, updateParams],
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (isStaged) {
        // No router.push to debounce against; update staged immediately so
        // tapping "Aplicar" right after typing always uses the latest value.
        updateParams({ q: value })
        return
      }
      debounceRef.current = setTimeout(() => {
        updateParams({ q: value })
      }, 300)
    },
    [updateParams, isStaged],
  )

  const clearAll = useCallback(() => {
    setSearchValue("")
    if (isStaged && onStagedChange) {
      onStagedChange(new URLSearchParams())
      return
    }
    startTransition(() => {
      router.push("/aeronaves")
    })
  }, [router, startTransition, isStaged, onStagedChange])

  return (
    <div
      aria-busy={isPending}
      className={cn("flex flex-col", isPending && FILTER_PENDING_CLASSES)}
    >
      {/* Header — hidden in staged mode (sheet has its own header + footer Limpar) */}
      {!isStaged && (
      <div className="flex items-center justify-between border-b-[0.5px] border-slate-200 py-4">
        <div className="font-heading text-sm font-medium text-navy-900">
          {t.catalog.filters}
        </div>
        <button
          type="button"
          onClick={clearAll}
          className="cursor-pointer text-xs text-slate-500 transition-colors hover:text-navy-800"
        >
          {t.catalog.clear}
        </button>
      </div>
      )}

      {/* Search */}
      <FilterSection title={t.catalog.search}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t.catalog.searchPlaceholder}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-9 pl-9 text-sm"
          />
        </div>
      </FilterSection>

      {/* Categorias */}
      {!isHelicopteros && visibleCategorias.length > 0 && (
        <FilterSection title={t.catalog.category}>
          {visibleCategorias.map((cat) => (
            <CheckRow
              key={cat.slug}
              id={`cat-${cat.slug}`}
              label={resolveI18n(cat.nome, locale)}
              checked={categoriaSel.includes(cat.slug)}
              onCheckedChange={(checked) =>
                toggleIn("categoria", cat.slug, checked)
              }
            />
          ))}
        </FilterSection>
      )}

      {/* Fabricantes */}
      {visibleFabricantes.length > 0 && (
        <FilterSection title={t.catalog.manufacturer}>
          <div
            className={
              isStaged
                ? "-mx-1 px-1"
                : "-mx-1 max-h-[200px] overflow-y-auto px-1"
            }
          >
            {visibleFabricantes.map((fab) => (
              <CheckRow
                key={fab.slug}
                id={`fab-${fab.slug}`}
                label={getFabricanteLabel(fab, t)}
                checked={fabricanteSel.includes(fab.slug)}
                onCheckedChange={(checked) =>
                  toggleIn("fabricante", fab.slug, checked)
                }
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Modelos */}
      {visibleModelos.length > 0 && (
        <FilterSection title={t.catalog.model}>
          <div
            className={
              isStaged
                ? "-mx-1 px-1"
                : "-mx-1 max-h-[220px] overflow-y-auto px-1"
            }
          >
            {visibleModelos.map((m) => (
              <CheckRow
                key={m.slug}
                id={`mod-${m.slug}`}
                label={`${getFabricanteLabel({ slug: m.fabricante_slug, nome: m.fabricante_nome }, t)} ${resolveI18n(m.nome, locale)}`}
                checked={modeloSel.includes(m.slug)}
                onCheckedChange={(checked) =>
                  toggleIn("modelo", m.slug, checked)
                }
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Localização */}
      {localizacoes.length > 0 && (
        <FilterSection title={t.catalog.location}>
          <div
            className={
              isStaged
                ? "-mx-1 px-1"
                : "-mx-1 max-h-[200px] overflow-y-auto px-1"
            }
          >
            {localizacoes.map((loc) => (
              <CheckRow
                key={loc}
                id={`loc-${loc}`}
                label={loc}
                checked={localizacaoSel.some(
                  (s) => s.toLowerCase() === loc.toLowerCase(),
                )}
                onCheckedChange={(checked) =>
                  toggleLocalizacao(loc, checked)
                }
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Year */}
      <FilterSection title={t.catalog.year}>
        <RangeDualSlider
          min={YEAR_MIN}
          max={YEAR_MAX}
          step={1}
          value={[anoMin, anoMax]}
          onCommit={([lo, hi]) =>
            updateParams({
              ano_min: lo > YEAR_MIN ? String(lo) : "",
              ano_max: hi < YEAR_MAX ? String(hi) : "",
            })
          }
          format={(n) => String(n)}
          ariaLabel={t.catalog.year}
        />
      </FilterSection>

      {/* Others */}
      <FilterSection title={t.catalog.others}>
        <CheckRow
          id="destaque"
          label={t.catalog.featured}
          checked={apenasDestaque}
          onCheckedChange={(c) => updateParams({ destaque: c ? "1" : "" })}
        />
        <CheckRow
          id="com-fotos"
          label={t.catalog.withPhotos}
          checked={comFotos}
          onCheckedChange={(c) => updateParams({ com_fotos: c ? "1" : "" })}
        />
      </FilterSection>

      {/* CTA card — hidden in staged mode (sheet footer takes over) */}
      {!isStaged && (
        <div className="py-5">
          <Link
            href="/contato"
            className="flex items-center justify-between rounded-[10px] border-[0.5px] border-navy-200 bg-navy-50 px-4 py-3.5 transition-colors hover:bg-navy-50/70"
          >
            <div>
              <div className="text-xs font-medium text-navy-900">
                {t.catalog.notFoundTitle}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-600">
                {t.catalog.notFoundBody}
              </div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-navy-800" />
          </Link>
        </div>
      )}
    </div>
  )
}

function FilterSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="border-b-[0.5px] border-slate-200 py-[18px] last:border-b-0">
      <div className="mb-3 text-xs font-medium uppercase tracking-[0.08em] text-slate-700">
        {title}
      </div>
      {children}
    </div>
  )
}

function CheckRow({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-2.5 text-[13px] text-slate-700 transition-colors hover:bg-slate-50 md:py-1.5"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        className="size-4 border-slate-300 data-[state=checked]:border-navy-800 data-[state=checked]:bg-navy-800"
      />
      <span className="flex-1">{label}</span>
    </label>
  )
}
